import React from 'react';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { MedicationFormData, foodInstructionOptions } from '@/components/medication/types/medication';
import AnimatedContainer from '@/components/ui-elements/AnimatedContainer';

// Color options for visual identification
const MEDICATION_COLORS = [
  { value: 'red', label: 'Red', bg: 'bg-red-500' },
  { value: 'blue', label: 'Blue', bg: 'bg-blue-500' },
  { value: 'green', label: 'Green', bg: 'bg-green-500' },
  { value: 'purple', label: 'Purple', bg: 'bg-purple-500' },
  { value: 'pink', label: 'Pink', bg: 'bg-pink-500' },
  { value: 'orange', label: 'Orange', bg: 'bg-orange-500' },
  { value: 'cyan', label: 'Cyan', bg: 'bg-cyan-500' },
  { value: 'amber', label: 'Amber', bg: 'bg-amber-500' },
  { value: 'teal', label: 'Teal', bg: 'bg-teal-500' },
  { value: 'lime', label: 'Lime', bg: 'bg-lime-500' },
  { value: 'indigo', label: 'Indigo', bg: 'bg-indigo-500' },
  { value: 'violet', label: 'Violet', bg: 'bg-violet-500' }
];

interface AdditionalInfoStepProps {
  formData: MedicationFormData;
  updateFormData: (fieldName: keyof MedicationFormData, value: any) => void;
}

/**
 * Step 4: Configure additional medication options like food instructions and refill tracking
 */
const AdditionalInfoStep: React.FC<AdditionalInfoStepProps> = ({ formData, updateFormData }) => {
  return (
    <AnimatedContainer animation="fade-in" className="space-y-6">
      <div className="space-y-3">
        <Label className="text-sm font-medium">When to Take</Label>
        <div className="grid grid-cols-1 gap-2">
          {foodInstructionOptions.map((option) => (
            <div
              key={option.value}
              className={`h-12 flex items-center justify-center rounded-md transition-colors px-3 ${formData.foodInstructions === option.value 
                ? 'bg-purple-600 text-white border border-purple-600' 
                : 'bg-gray-50 border border-gray-200 dark:bg-gray-900/10 dark:border-gray-800 hover:bg-gray-100 dark:hover:bg-gray-900/20 cursor-pointer'}`}
              onClick={() => updateFormData('foodInstructions', option.value)}
              role="button"
              aria-pressed={formData.foodInstructions === option.value}
              tabIndex={0}
            >
              {option.label}
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-3">
        <Label className="text-sm font-medium">Color Tag (for visual identification)</Label>
        <div className="grid grid-cols-6 gap-2">
          {MEDICATION_COLORS.map((color) => (
            <div
              key={color.value}
              className={`h-8 w-8 rounded-full cursor-pointer ${color.bg} ${
                formData.color === color.value 
                  ? 'ring-2 ring-offset-2 ring-primary' 
                  : 'hover:opacity-80'
              }`}
              title={color.label}
              onClick={() => updateFormData('color', color.value)}
              role="button"
              tabIndex={0}
              aria-label={`Select ${color.label} color`}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  updateFormData('color', color.value);
                }
              }}
            />
          ))}
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label htmlFor="refill-tracking" className="text-sm font-medium">
            Track Refills
          </Label>
          <Switch
            id="refill-tracking"
            checked={formData.refillTracking || false}
            onCheckedChange={(checked) => updateFormData('refillTracking', checked)}
          />
        </div>
        
        {formData.refillTracking && (
          <div className="space-y-4 pl-1 pt-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="current-supply" className="text-sm font-medium">
                  Current Supply
                </Label>
                <Input
                  id="current-supply"
                  type="number"
                  min={0}
                  value={formData.refillAmount || 0}
                  onChange={(e) => updateFormData('refillAmount', parseInt(e.target.value) || 0)}
                  className="w-full"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="refill-threshold" className="text-sm font-medium">
                  Refill Threshold (days)
                </Label>
                <Input
                  id="refill-threshold"
                  type="number"
                  min={1}
                  value={formData.refillThreshold || 7}
                  onChange={(e) => updateFormData('refillThreshold', parseInt(e.target.value) || 7)}
                  className="w-full"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="refills-remaining" className="text-sm font-medium">
                Refills Remaining
              </Label>
              <Input
                id="refills-remaining"
                type="number"
                min={0}
                value={formData.refillsRemaining || 0}
                onChange={(e) => updateFormData('refillsRemaining', parseInt(e.target.value) || 0)}
                className="w-full"
              />
            </div>
          </div>
        )}
      </div>

      <div className="space-y-3">
        <Label htmlFor="notes" className="text-sm font-medium">
          Additional Notes (Optional)
        </Label>
        <Textarea
          id="notes"
          value={formData.notes || ''}
          onChange={(e) => updateFormData('notes', e.target.value)}
          placeholder="Add any additional instructions or notes"
          className="min-h-[100px]"
        />
      </div>
    </AnimatedContainer>
  );
};

export default AdditionalInfoStep;
